<template>
  <div
    class="flex flex-col"
    :class="!webviewStore.isActive && 'h-dvh'"
    :style="styles"
  >
    <WebviewNavbar v-if="webviewStore.isActive"> Upgrade to Pro </WebviewNavbar>
    <BasicNavbar v-else />

    <div
      ref="container"
      class="min-h-0 flex-1 overflow-auto px-4 pt-[30px]"
      :class="!webviewStore.isActive && 'pb-3'"
    >
      <RouterView v-if="canRenderUpgrade" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useBillingStore } from '@/features/billing/store'
import { useUserStore } from '@/shared/stores/user'
import { UserPermission } from '@/shared/graphql/legacy/generated'
import { useRouter } from 'vue-router'
import { useWebviewStore } from '@/shared/stores/webview'
import { useDevice } from '@/shared/composables/useDevice'
import WebviewNavbar from '@/shared/components/WebviewNavbar.vue'
import BasicNavbar from '@/shared/components/BasicNavbar.vue'

const device = useDevice()
const router = useRouter()
const billingStore = useBillingStore()
const userStore = useUserStore()
const webviewStore = useWebviewStore()

const container = ref<HTMLElement>()

const user = computed(() => userStore.user)
const canRenderUpgrade = computed(() => {
  if (webviewStore.isActive) return true
  if (!user.value) return false
  if (!user.value.permissions.includes(UserPermission.Pro)) return true

  return !!billingStore.billing && billingStore.isBasicMembership
})

watch(
  [user, () => billingStore.billing],
  ([currentUser, currentBilling]) => {
    if (
      currentBilling &&
      currentUser?.permissions.includes(UserPermission.Pro) &&
      !billingStore.isBasicMembership
    ) {
      router.replace('/home')
    }
  },
  { immediate: true }
)

const styles = computed(() => ({
  ...(webviewStore.isActive && {
    paddingTop: `${webviewStore.topSafeAreaHeight}px`,
    height: `${device.viewportHeight.value}px`,
  }),
}))

onMounted(() => {
  if (!webviewStore.isActive) return
  container.value?.addEventListener('scroll', webviewStore.handleScroll)
})

onBeforeUnmount(() => {
  container.value?.removeEventListener('scroll', webviewStore.handleScroll)
})
</script>
